import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import isEmpty from 'lodash/isEmpty';
import { getCertificates, getCertificateSuggestions, getMe } from 'platform-components/src/components/user/selectors';
import { createSelector } from 'reselect';

import { getSlugFromProps } from 'utils/selectors/selectors';

import { getIsInSandbox } from '../../store/external-lms/selectors';

import { CertificateModalStore } from './typings';

/**
 * @param {Object} state
 * @return {Object}
 */
const certificatesModalStateSelector = <T extends { certificatesModal: CertificateModalStore }>(state: T) =>
    state.certificatesModal;

/**
 * @param {Object} storeState
 * @return {Array}
 */
export const getLocalities = createSelector([certificatesModalStateSelector], (state) => state.localities);

/**
 * @param {Object} storeState
 * @return {string}
 */
export const statusOfAcceptSuggestion = createSelector(
    certificatesModalStateSelector,
    (state) => state.statusOfAcceptSuggestion
);

/**
 * @param {Object} storeState
 * @return {boolean}
 */
export const canChangeData = createSelector(
    statusOfAcceptSuggestion,
    (status) => status === LOADING_STATUSES.UNSENT || status === LOADING_STATUSES.ERROR
);

/**
 * @param {Object} storeState
 * @return {Array}
 */
export const getShownSuggestions = createSelector(certificatesModalStateSelector, (state) => state.shownSuggestions);

/**
 * @param {Object} storeState
 * @param {string} certificateSlug
 * @return {boolean}
 */
export const isSuggestionAccepted = createSelector([getShownSuggestions, getSlugFromProps], (suggestions, slug) =>
    slug ? suggestions.includes(slug) : false
);

/**
 * @param {Object} storeState
 * @return {string}
 */
export const getEmail = createSelector([getMe], (user) => user?.email || '');

/**
 * @param {Object} storeState
 * @return {string}
 */
export const getFirstSuggestedCertificateSlug = createSelector(getCertificateSuggestions, (suggestions) =>
    suggestions ? suggestions[0] : null
);

/**
 * Returns true if there is given slug in suggestions list
 * @param storeState
 * @param certificateSlug
 * @return {Boolean}
 */
export const getHasSlugSuggestion = createSelector(
    [getCertificateSuggestions, getSlugFromProps],
    (certificates, slug) => (slug ? certificates?.includes(slug) : false)
);

/**
 * @param {Object} storeState
 * @param {string} certificateSlug
 * @return {Object}
 */
export const getCertificateStats = createSelector([getCertificates, getSlugFromProps], (certificates, slug) =>
    slug && certificates ? certificates[slug] || null : null
);
/**
 * @param {Object} storeState
 * @param {string} certificateSlug
 * @param {string} search
 * @return {boolean}
 */
export const shouldShowBannerCertificateModal = createSelector(
    [getCertificateSuggestions, getSlugFromProps, getIsInSandbox],
    (suggestions, certificateSlug, isInSandbox) =>
        Boolean(certificateSlug && (suggestions || []).includes(certificateSlug) && !isInSandbox)
);

/**
 * @param {Object} storeState
 * @param {string} certificateSlug
 * @return {Object}
 */
export const shouldShowCertificateModal = createSelector(
    [getCertificateSuggestions, getCertificateStats],
    (suggestions, stats) => {
        const { last_cancel: lastCancel } = stats || {};
        return Boolean(suggestions && suggestions.length > 0 && !lastCancel);
    }
);

/**
 * @param {Object} storeState
 * @param {string} certificateSlug
 * @return {Number}
 */
export const getCertificateClosesCount = createSelector([getCertificateStats], (stats) => stats?.closes_count || null);

export const getHasAgreedToReceiveCertificate = createSelector(
    [getCertificates, getSlugFromProps],
    (certificates, slug) => Boolean(slug && certificates && certificates[slug]?.last_agree)
);

/**
 * Возвращает статус установки секции с сертификатами
 * Без нее запрос на сертификат не сработает
 * @param {Object} storeState
 * @return {LOADING_STATUSES}
 */
export const getStatusOfCertificateSection = createSelector(
    certificatesModalStateSelector,
    (state) => state.statusOfCertificateSection
);

/**
 * Returns true if should iniciate certificates section
 * @param storeState
 * @param certificateSlug
 * @return {Boolean}
 */
export const getShouldSetCertificateSection = createSelector(
    [getCertificates, getCertificateStats, getHasSlugSuggestion],
    (certificates, certificateData, hasSuggestion) => {
        return (certificates && isEmpty(certificates)) || (isEmpty(certificateData || {}) && !hasSuggestion);
    }
);
